"use client";
import { MdInsights } from "@onemind-services-llc/react-icons-ng/md";
import { FiCpu } from "@onemind-services-llc/react-icons-ng/fi";
import { GiProcessor } from "@onemind-services-llc/react-icons-ng/gi";
import { Box, HStack, Stack } from "@chakra-ui/react";
import { useTextVariant } from "@/theme/hooks/useSectionStyle";
import { Text } from "@/elements";

type GPUProps = {
  idealFor: string;
  performanceHighlights: string;
  config: {
    memory: string;
    performance: string;
    power: string;
    bandwidth: string;
  };
};

const DescComp = (props: GPUProps) => {
  const textVariant = useTextVariant();
  const { idealFor, performanceHighlights, config } = props;
  return (
    <Box
      p={6}
      mt={4}
      borderWidth="1px"
      borderRadius="2xl"
      boxShadow="sm"
      bg={"white"}
      transition="all 0.3s ease"
      _hover={{ boxShadow: "lg", transform: "translateY(-2px)" }}
    >
      <Stack gap={4} color="text.100">
        <HStack justifyContent={"space-between"}>
          <Box>
            <Text fontWeight="bold" fontSize={"sm"}>
              GPU Memory
            </Text>
            <Text variant={textVariant}>{config.memory}</Text>
          </Box>
          <Box>
            <Text fontWeight="bold" fontSize={"sm"}>
              FP32 Performance
            </Text>
            <Text variant={textVariant}>{config.performance}</Text>
          </Box>
        </HStack>
        <HStack justifyContent={"space-between"}>
          <Box>
            <Text fontWeight="bold" fontSize={"sm"}>
              Max Power Draw
            </Text>
            <Text variant={textVariant}>{config.power}</Text>
          </Box>
          <Box>
            <Text fontWeight="bold" fontSize={"sm"}>
              Bandwidth
            </Text>
            <Text variant={textVariant}>{config.bandwidth}</Text>
          </Box>
        </HStack>
        <Box>
          <Text fontWeight="bold" fontSize={"sm"}>
            Ideal For:
          </Text>
          <Text variant={textVariant}>{idealFor}</Text>
        </Box>
        <Box>
          <Text fontWeight="bold" fontSize={"sm"}>
            Performance Highlights:
          </Text>
          <Text variant={textVariant}>{performanceHighlights}</Text>
        </Box>
      </Stack>
    </Box>
  );
};

export const MODELS_CONSTANTS = [
  {
    icon: MdInsights,
    title: "T4",
    text: "(Efficient, Versatile GPU)",
    children: (
      <DescComp
        idealFor="AI inference, analytics, interactive apps, and low-latency workloads"
        performanceHighlights="Efficient for NLP, vision models, and video processing "
        config={{
          memory: "16GB GDDR6",
          performance: "8.1 TFLOPS",
          power: "8.1 TFLOPS",
          bandwidth: "320 GB/s",
        }}
      />
    ),
  },
  {
    icon: FiCpu,
    title: "L4",
    text: "(Balanced Compute for AI & Media)",
    children: (
      <DescComp
        idealFor="Balanced compute for AI, rendering, desktop apps"
        performanceHighlights="Versatile for mixed AI, 2D/3D workloads, and media tasks."
        config={{
          memory: "24 GB GDDR6",
          performance: "30 TFLOPS",
          power: "72W",
          bandwidth: "300 GB/s",
        }}
      />
    ),
  },
  {
    icon: GiProcessor,
    title: "L40",
    text: "(High-End Performance for Deep Learning",
    children: (
      <DescComp
        idealFor="Heavy-duty training, simulation, generative AI "
        performanceHighlights="Optimal for deep learning, 3D workflows, and compute-intensive apps "
        config={{
          memory: "48 GB GDDR6",
          performance: "91.6 TFLOPS",
          power: "300W",
          bandwidth: "900 GB/s",
        }}
      />
    ),
  },
];
